// @file Dashboard settings store
// It wraps the dashboard settings Vuex store into a Pinia store.
import device from '@@/bits/device'
import { getVuexStore } from '@@/bits/pinia'
import type { DashboardSettingsState } from '@@/vuexstore/dashboard_settings'
import type { Library, LibraryId, Tenant, User, UserOptions } from '@@/types'
import { defineStore } from 'pinia'
import { computed } from 'vue'

export const useDashboardSettingsStore = defineStore('dashboardSettings', () => {
  // State

  // Getters
  const user = computed((): User => getVuexStore<DashboardSettingsState>()?.getters.user ?? { id: null })
  const userOptions = computed((): UserOptions => getVuexStore<DashboardSettingsState>()?.getters.userOptions)
  const tenant = computed((): Tenant => getVuexStore<DashboardSettingsState>()?.getters.tenant ?? { id: null })
  const libraries = computed((): Library[] => getVuexStore<DashboardSettingsState>()?.getters.libraries ?? [])
  const libraryId = computed((): LibraryId => getVuexStore<DashboardSettingsState>()?.getters.libraryId)
  const isApp = computed((): boolean => device.app)
  const isElectronApp = computed((): boolean => device.electronApp)
  const isNeonTier = computed((): boolean => getVuexStore<DashboardSettingsState>()?.getters.isNeonTier)
  const isCancellingSoon = computed((): boolean => getVuexStore<DashboardSettingsState>()?.getters.isCancellingSoon)
  const isBillingInitialized = computed(
    (): boolean => getVuexStore<DashboardSettingsState>()?.getters.isBillingInitialized,
  )
  const ownedLibraries = computed((): Library[] => getVuexStore<DashboardSettingsState>()?.getters.ownedLibraries)
  const trialDaysLeft = computed((): number => getVuexStore<DashboardSettingsState>()?.getters.trialDaysLeft)

  // Actions
  function changeConnectivity(isOnline: boolean): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('changeConnectivity', isOnline, { root: true })
  }

  function resizeWindow(payload: { width: number; height: number }): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('browser/resizeWindow', payload, { root: true })
  }

  function resizeVisualViewport(payload: { width: number; height: number; scale: number }): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('browser/resizeVisualViewport', payload, { root: true })
  }

  function toggleDarkMode(isDarkMode: boolean): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('browser/toggleDarkMode', isDarkMode, { root: true })
  }

  function goBack(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('goBack', { root: true })
  }

  function updateCurrentTenant(payload: Partial<Tenant>): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('updateCurrentTenant', payload, { root: true })
  }

  function updateUserOptions(payload: Partial<UserOptions>): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('updateUserOptions', payload, { root: true })
  }

  function startOffboarding(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('startOffboarding', { root: true })
  }

  function verifyIdentity(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('verifyIdentity', { root: true })
  }

  return {
    // State

    // Getters
    user,
    userOptions,
    tenant,
    libraries,
    libraryId,
    isApp,
    isElectronApp,
    isNeonTier,
    isCancellingSoon,
    isBillingInitialized,
    ownedLibraries,
    trialDaysLeft,

    // Actions
    changeConnectivity,
    resizeWindow,
    resizeVisualViewport,
    toggleDarkMode,
    goBack,
    updateCurrentTenant,
    updateUserOptions,
    startOffboarding,
    verifyIdentity,
  }
})
